import React, { useState, useEffect, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column, ColumnBodyOptions } from 'primereact/column';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Toast } from 'primereact/toast';

import arrearAddresssService from '../../service/api/ArrearAddresssService';
import { Dialog } from 'primereact/dialog';
import { useTranslation } from 'react-i18next';
import { ArrearAddress } from '../../types/ArrearAddress';
import { FileUpload } from 'primereact/fileupload';

export const ArrearAddressManagement = () => {
  const toast = useRef<any>();

  const [globalFilter, setGlobalFilter] = useState('');
  const [loading, setLoading] = useState(true);

  const [arrearAddresss, setArrearAddresss] = useState<ArrearAddress[]>(null);

  const [resultDialogVisible, setResultDialogVisible] = useState(false);

  const [importErrors, setImportErrors] = useState([]);

  const { t } = useTranslation();
  const fileUploader = useRef(null);

  const loadData = () => {
    setLoading(true);
    Promise.all([arrearAddresssService.getAll()]).then((response) => {
      const _arrearAddresss = response[0];
      setArrearAddresss(_arrearAddresss);
      setLoading(false);
    });
  };

  const importData = async (event) => {
    const [f] = event.files;
    await arrearAddresssService
      .import(f)
      .then((result) => {
        const errors = result?.data?.errors;
        if (errors?.length > 0) {
          setImportErrors(errors);
          setResultDialogVisible(true);
        } else {
          toast.current.show({
            severity: 'success',
            summary: t('common.dataImportSuccess'),
            life: 4000,
          });
        }
      })
      .catch(() => {
        toast.current.show({
          severity: 'error',
          summary: t('common.dataImportError'),
          detail: t('common.dataImportErrorDetails'),
          life: 4000,
        });
      })
      .finally(() => {
        loadData();
      });
    fileUploader.current.clear();
  };

  useEffect(() => {
    loadData();
  }, []);

  const arrearAddresssTableHeader = (
    <div className="table-header">
      {t('arrearAddresss.header')}
      <span className="flex ">
        <span className="p-input-icon-left">
          <i className="pi pi-search" />
          <InputText
            value={globalFilter}
            onChange={(e) => setGlobalFilter(e.target.value)}
            placeholder={t('common.search')}
          />
        </span>
        <>
          <FileUpload
            ref={fileUploader}
            mode="basic"
            accept=".csv"
            maxFileSize={1000000}
            className="ml-2"
            customUpload
            auto
            uploadHandler={importData}
            chooseOptions={{
              label: t('common.import'),
              icon: 'pi pi-fw pi-download',
            }}
          />
        </>
      </span>
    </div>
  );

  const hideResultDialog = () => {
    setResultDialogVisible(false);
  };

  const resultDialogFooter = (
    <>
      <Button
        label={t('common.close')}
        icon="pi pi-check"
        className="p-button-text"
        onClick={() => hideResultDialog()}
      />
    </>
  );

  const bodyTemplate = (data: ArrearAddress, props: ColumnBodyOptions) => {
    return <>{data[props.field]}</>;
  };

  return (
    <div className="grid crud-demo">
      <div className="col-12">
        <div className="card">
          <DataTable
            value={arrearAddresss}
            paginator
            className="p-datatable-customers"
            rows={10}
            dataKey="id"
            rowHover
            globalFilter={globalFilter}
            emptyMessage={t('arrearAddresss.empty_message')}
            loading={loading}
            header={arrearAddresssTableHeader}
          >
            <Column
              field="originalAddress"
              header={t('arrearAddresss.original_address')}
              sortable
              body={bodyTemplate}
            ></Column>
            <Column field="street" header={t('arrearAddresss.street')} sortable body={bodyTemplate}></Column>
            <Column
              field="houseNumberRangeStart"
              header={t('arrearAddresss.house_number_range_start')}
              sortable
              body={bodyTemplate}
            ></Column>
            <Column
              field="houseNumberRangeEnd"
              header={t('arrearAddresss.house_number_range_end')}
              sortable
              body={bodyTemplate}
            ></Column>
            <Column
              field="houseNumberSuffixStart"
              header={t('arrearAddresss.house_number_suffix_start')}
              sortable
              body={bodyTemplate}
            ></Column>
            <Column
              field="houseNumberSuffixEnd"
              header={t('arrearAddresss.house_number_suffix_end')}
              sortable
              body={bodyTemplate}
            ></Column>
            <Column
              field="apartmentNumberRangeStart"
              header={t('arrearAddresss.apartment_number_range_start')}
              sortable
              body={bodyTemplate}
            ></Column>
          </DataTable>
          <Dialog
            visible={resultDialogVisible}
            className="p-fluid"
            header={
              <span style={{ display: 'flex', alignItems: 'center' }}>
                <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: '2rem' }} />
                {importErrors?.length ? t('common.errorOnAddresses') : t('common.success')}
              </span>
            }
            modal
            footer={resultDialogFooter}
            onHide={hideResultDialog}
            breakpoints={{ '896px': '90vw' }}
            style={{ minWidth: '550px' }}
          >
            <div className="confirmation-content">
              {importErrors?.map((e) => (
                <div key={e}>{e}</div>
              ))}
            </div>
          </Dialog>
        </div>
      </div>

      <Toast ref={toast} />
    </div>
  );
};
